import * as yup from "yup";

import Button from "../Button";
import Typography from "../Typography";
import {
    CreateEmissionsSourceFormValues,
    buildStep2ValidationFields,
} from "./FormValues";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import FormRadioGroup, { FormRadioGroupOption } from "../FormRadioGroup";
import { isMaterial, notMaterial } from "../../constants";
import FormTextInput from "../FormTextInput";
import EmissionsSourceItemDetail from "../EmissionsSourceItemDetailForm";
import {
    EmissionsSourceItems,
} from "../../api/models/EmissionsSource";

export type Step2TypeProps = {
    onSubmit: (values: Step2TypeValues, finished: boolean) => void;
    onBack: (values: Step2TypeValues) => void;
    onCancel: () => void;
    defaultValues: Step2TypeValues;
    emissionsSourceItems: EmissionsSourceItems | null;
    isLoadingEmissionsSourceItems: boolean;
};

export type Step2TypeValues = Pick<
    CreateEmissionsSourceFormValues,
    | "categoryId"
    | "typeId"
    | "itemId"
    | "isMaterial"
    | "justification"
    | "enterpriseValue"
    | "outstandingAmount"
>;

const options: FormRadioGroupOption[] = [
    {
        label: "Yes",
        value: isMaterial,
    },
    {
        label: "No",
        value: notMaterial,
    },
];

const Step2Type = ({
    onSubmit,
    onBack,
    onCancel,
    defaultValues,
    emissionsSourceItems,
    isLoadingEmissionsSourceItems,
}: Step2TypeProps) => {
    const validationSchema: yup.SchemaOf<Step2TypeValues> | null = yup.object(
        buildStep2ValidationFields()
    );

    const methods = useForm<Step2TypeValues>({
        mode: "onSubmit",
        resolver: validationSchema ? yupResolver(validationSchema) : undefined,
        defaultValues,
    });

    const selectedIsMaterial = methods.watch("isMaterial");

    const handleBackClick = async () => {
        await methods.trigger();

        if (methods.formState.isValid) {
            onBack(methods.getValues());
        }
    };

    const handleSubmit = (values: Step2TypeValues) => {
        const finished = values.isMaterial === notMaterial;
        onSubmit(values, finished);
    };

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(handleSubmit)}
                className="space-y-4 rounded-xl bg-surface-light-1 p-6"
            >
                <Typography
                    variant="title-large"
                    component="h2"
                    className="mb-6"
                >
                    Attribution
                </Typography>
                <FormTextInput
                    required
                    label="Enterprise Value (£)"
                    placeholder="Enter enterprise value"
                    {...methods.register("enterpriseValue")}
                    type="number"
                    error={methods.formState.errors.enterpriseValue}
                />
                <FormTextInput
                    required
                    label="Outstanding Amount (£)"
                    placeholder="Enter outstanding amount"
                    {...methods.register("outstandingAmount")}
                    type="number"
                    error={methods.formState.errors.outstandingAmount}
                />
                <EmissionsSourceItemDetail
                    emissionsSourceItems={emissionsSourceItems}
                    isLoadingEmissionsSourceItems={
                        isLoadingEmissionsSourceItems
                    }
                />
                <Controller
                    name="isMaterial"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormRadioGroup
                            required
                            label="Is material?"
                            options={options}
                            error={methods.formState.errors.isMaterial}
                            defaultValue={value}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
                <div className="flex justify-end pt-4">
                    <Button
                        variant="transparent"
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="mr-4"
                        variant="outline"
                        type="button"
                        onClick={handleBackClick}
                    >
                        Previous
                    </Button>
                    <Button type="submit">
                        {selectedIsMaterial === notMaterial
                            ? "Create asset"
                            : "Next"}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default Step2Type;
